import React, { useEffect, useMemo, useState } from 'react';
import { Table, Card, Typography, Space, Tag, InputNumber, Row, Col, Form, Select, Button, message, Input, Modal, Grid, DatePicker, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, ArrowUpOutlined, ArrowDownOutlined, EditOutlined, CalendarOutlined } from '@ant-design/icons';
import client from '../api/client';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';
import ExportButton from '../components/ExportButton';

const { RangePicker } = DatePicker;

const { Title } = Typography;

const StockCardPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isStaff = user?.role === 'STAFF';
    const [levels, setLevels] = useState([]);
    const [ledger, setLedger] = useState([]);
    const [items, setItems] = useState([]);
    const [branches, setBranches] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [form] = Form.useForm();
    const [searchQuery, setSearchQuery] = useState('');
    const [stockCategoryFilter, setStockCategoryFilter] = useState('all');
    const [adjustmentCategoryFilter, setAdjustmentCategoryFilter] = useState(null);
    const [scopeBranchId, setScopeBranchId] = useState('all');
    const [scopeLocationId, setScopeLocationId] = useState('all');
    const [palayLots, setPalayLots] = useState([]);
    const [ledgerDateRange, setLedgerDateRange] = useState([null, null]);

    const fetchStock = async (branchId = scopeBranchId, locationId = scopeLocationId, dateRange = ledgerDateRange) => {
        setLoading(true);
        try {
            const params = {};
            if (branchId && branchId !== 'all') params.branch_id = branchId;
            if (locationId && locationId !== 'all') params.location_id = locationId;
            if (dateRange?.[1]) params.as_of_date = dateRange[1].format('YYYY-MM-DD');
            const { data } = await client.get('/inventory/stock-levels', { params });
            setLevels(data);
        } catch (error) {
            console.error('Failed to fetch stock');
        } finally {
            setLoading(false);
        }
    };

    const fetchFormData = async () => {
        try {
            const [itemsRes, branchesRes, locationsRes] = await Promise.all([
                client.get('/master/items'),
                client.get('/master/branches'),
                client.get('/master/locations'),
            ]);
            setItems(itemsRes.data);
            setBranches(branchesRes.data);
            setLocations(locationsRes.data);
        } catch (error) {
            console.error('Failed to fetch form data');
        }
    };

    const fetchLedger = async (itemId, branchId = scopeBranchId, locationId = scopeLocationId) => {
        try {
            const params = {};
            if (itemId) params.item_id = itemId;
            if (branchId && branchId !== 'all') params.branch_id = branchId;
            if (locationId && locationId !== 'all') params.location_id = locationId;
            const { data } = await client.get('/inventory/ledger', { params });
            setLedger(data);
        } catch (error) {
            console.error('Failed to fetch ledger');
        }
    };


    useEffect(() => {
        fetchFormData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        if (scopeBranchId !== 'all' && scopeLocationId !== 'all') {
            const belongs = locations.some((loc) => Number(loc.id) === Number(scopeLocationId) && Number(loc.branch_id) === Number(scopeBranchId));
            if (!belongs) {
                setScopeLocationId('all');
                return;
            }
        }
        fetchStock(scopeBranchId, scopeLocationId, ledgerDateRange);
        fetchLedger(selectedItem?.id, scopeBranchId, scopeLocationId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopeBranchId, scopeLocationId, locations]);

    useEffect(() => {
        fetchStock(scopeBranchId, scopeLocationId, ledgerDateRange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ledgerDateRange]);

    const isPalayItem = (itemId) => {
        const item = items.find((i) => i.id === itemId);
        const slug = item?.category?.slug?.toLowerCase?.() || '';
        const name = item?.category?.name?.toLowerCase?.() || '';
        return slug === 'palay' || name === 'palay';
    };

    const getItemUnitLabel = (itemId) => {
        const item = items.find((i) => Number(i.id) === Number(itemId));
        return item?.unit?.abbreviation || item?.unit?.name || 'units';
    };

    const getLedgerUnitLabel = (record) => {
        return record?.item?.unit?.abbreviation
            || record?.item?.unit?.name
            || getItemUnitLabel(record?.item_id);
    };

    const getLedgerReference = (record) => {
        if (record.batch_no) return record.batch_no;
        if (record.sales_order?.order_number) return record.sales_order.order_number;
        if (record.remarks) {
            const match = record.remarks.match(/[-–]\s*(.+)$/);
            if (match) return match[1].trim();
        }
        return '-';
    };

    const getLedgerCustomerName = (record) => {
        if (record.transaction_type === 'SALES') {
            return record.sales_order?.customer?.name || 'Walk-in';
        }
        if (record.transaction_type === 'STOCK_IN') {
            return record.palay_purchase?.partner?.name || '-';
        }
        return '-';
    };

    const getLedgerTransactionDate = (record) => {
        if (record.transaction_type === 'SALES') {
            return record.sales_order?.order_date || record.created_at;
        }
        if (record.transaction_type === 'STOCK_IN') {
            return record.palay_purchase?.transaction_date || record.created_at;
        }
        return record.created_at;
    };

    const getLedgerEncodedAt = (record) => {
        if (record.transaction_type === 'SALES') return record.sales_order?.created_at;
        if (record.transaction_type === 'STOCK_IN') return record.palay_purchase?.created_at;
        return null;
    };

    const getLedgerConfirmedAt = (record) => {
        if (record.transaction_type === 'SALES') {
            return record.sales_order?.confirmed_at || record.created_at;
        }
        if (record.transaction_type === 'STOCK_IN') {
            return record.palay_purchase?.journal_entry?.created_at || record.created_at;
        }
        return record.created_at;
    };

    const [allPalayLots, setAllPalayLots] = useState([]);

    const fetchPalayLots = async () => {
        try {
            const [activeLots, allLots] = await Promise.all([
                client.get('/inventory/palay-lots'),
                client.get('/inventory/palay-lots', { params: { all: 1 } }),
            ]);
            setPalayLots(activeLots.data || []);
            setAllPalayLots(allLots.data || []);
        } catch {
            setPalayLots([]);
            setAllPalayLots([]);
        }
    };

    const handleAddStock = async (values) => {
        setSubmitLoading(true);
        try {
            await client.post('/inventory/adjustments', values);
            const typeLabel = { STOCK_IN: 'Stock In', STOCK_OUT: 'Stock Out', CORRECTION: 'Correction' }[values.adjustment_type] || 'Adjustment';
            message.success(`${typeLabel} recorded successfully`);
            form.resetFields();
            setAdjustmentCategoryFilter(null);
            fetchStock();
            fetchPalayLots();
            if (selectedItem?.id === values.item_id) {
                fetchLedger(values.item_id);
            }
            setIsModalOpen(false);
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to adjust stock');
        } finally {
            setSubmitLoading(false);
        }
    };

    const branchId = Form.useWatch('branch_id', form);

    const filteredLocations = useMemo(() => {
        if (!branchId) {
            return locations;
        }
        return locations.filter((loc) => Number(loc.branch_id) === Number(branchId));
    }, [locations, branchId]);

    const itemCategories = useMemo(() => {
        const names = (items || [])
            .map((item) => item.category?.name)
            .filter(Boolean);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
    }, [items]);

    const filteredItems = useMemo(() => {
        if (!adjustmentCategoryFilter) return items;
        return items.filter((item) => item.category?.name === adjustmentCategoryFilter);
    }, [items, adjustmentCategoryFilter]);

    const filteredLevels = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return levels.filter((item) => {
            const matchesCategory = stockCategoryFilter === 'all' || item.category === stockCategoryFilter;
            const matchesSearch = !query || (
                String(item.id || '').includes(query) ||
                item.code?.toLowerCase().includes(query) ||
                item.name?.toLowerCase().includes(query) ||
                item.category?.toLowerCase().includes(query)
            );
            return matchesCategory && matchesSearch;
        });
    }, [levels, searchQuery, stockCategoryFilter]);

    const filteredLedger = useMemo(() => {
        const [from, to] = ledgerDateRange;
        const query = searchQuery.trim().toLowerCase();
        return ledger.filter((row) => {
            const matchesSearch = !query || [
                row.id,
                getLedgerReference(row),
                row.item?.name,
                row.transaction_type,
                row.location?.name,
                getLedgerCustomerName(row),
            ].some((value) => String(value || '').toLowerCase().includes(query));
            if (!matchesSearch) return false;

            // Use the business transaction's own date where a source record
            // exists; fall back to the ledger timestamp for standalone moves.
            const d = dayjs(getLedgerTransactionDate(row));
            if (from && d.isBefore(from.startOf('day'))) return false;
            if (to && d.isAfter(to.endOf('day'))) return false;
            return true;
        });
    }, [ledger, ledgerDateRange, searchQuery]);

    const getLedgerExportRows = () =>
        filteredLedger.map((r) => {
            const ref = r.batch_no || (() => {
                const match = r.remarks?.match(/[-–]\s*(.+)$/);
                return match ? match[1].trim() : '—';
            })();
            return [
                getLedgerTransactionDate(r) ? dayjs(getLedgerTransactionDate(r)).format('YYYY-MM-DD HH:mm') : '',
                getLedgerEncodedAt(r) ? dayjs(getLedgerEncodedAt(r)).format('YYYY-MM-DD HH:mm') : '',
                getLedgerConfirmedAt(r) ? dayjs(getLedgerConfirmedAt(r)).format('YYYY-MM-DD HH:mm') : '',
                r.id,
                getLedgerReference(r) || ref,
                getLedgerCustomerName(r),
                r.item?.name || '',
                r.transaction_type === 'IN' ? 'PURCHASE_IN' : r.transaction_type,
                Number(r.quantity),
                getLedgerUnitLabel(r),
                r.location?.name || '',
            ];
        });

    const totalStock = useMemo(() => {
        return filteredLevels.reduce((sum, item) => sum + Number(item.stock || 0), 0);
    }, [filteredLevels]);

    const lowStockCount = useMemo(() => {
        return filteredLevels.filter((item) => {
            const threshold = Number(item.reorder_level ?? 0);
            return threshold > 0 && Number(item.stock || 0) <= threshold;
        }).length;
    }, [filteredLevels]);

    const locationCoverage = useMemo(() => {
        if (scopeLocationId && scopeLocationId !== 'all') return 1;
        if (scopeBranchId && scopeBranchId !== 'all') {
            return locations.filter((loc) => Number(loc.branch_id) === Number(scopeBranchId)).length;
        }
        return locations.length;
    }, [locations, scopeBranchId, scopeLocationId]);

    const scopeLabel = useMemo(() => {
        const branchName = scopeBranchId === 'all'
            ? 'All branches'
            : (branches.find((b) => Number(b.id) === Number(scopeBranchId))?.name || 'Branch');
        const locationName = scopeLocationId === 'all'
            ? 'All locations'
            : (locations.find((l) => Number(l.id) === Number(scopeLocationId))?.name || 'Location');
        return `${branchName} • ${locationName}`;
    }, [branches, locations, scopeBranchId, scopeLocationId]);

    const getStockExportRows = () =>
        filteredLevels.map((item) => {
            const threshold = Number(item.reorder_level ?? 0);
            const stockValue = Number(item.stock || 0);
            const status = (threshold > 0 ? stockValue <= threshold : stockValue <= 0) ? 'Low Stock' : 'Healthy';
            return [item.code, item.name, item.category, stockValue, item.unit, threshold, status];
        });

    const stockColumns = [
        { title: 'Item Name', dataIndex: 'name', key: 'name' },
        { title: 'Category', dataIndex: 'category', key: 'category' },
        {
            title: 'Stock On Hand', dataIndex: 'stock', key: 'stock', render: (val, record) => (
                <Typography.Text strong style={{ color: val > 0 ? '#52c41a' : '#ef4444' }}>
                    {Number(val).toLocaleString()} {record.unit}
                </Typography.Text>
            )
        },
        {
            title: 'Status',
            dataIndex: 'stock',
            key: 'status',
            render: (_, record) => {
                const threshold = Number(record.reorder_level ?? 0);
                const stockValue = Number(record.stock || 0);
                const isLow = threshold > 0
                    ? stockValue <= threshold
                    : stockValue <= 0;
                return (
                    <Tag color={isLow ? 'error' : 'success'} style={{ borderRadius: 12 }}>
                        {isLow ? 'Low Stock' : 'Healthy'}
                    </Tag>
                );
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button type="default" size="small" onClick={() => { setSelectedItem(record); fetchLedger(record.id); }}>
                    View History
                </Button>
            ),
        },
    ];

    const ledgerColumns = [
        {
            title: 'Transaction Date',
            key: 'transaction_date',
            width: 150,
            onHeaderCell: () => ({ style: { paddingLeft: 12, paddingRight: 24 } }),
            onCell: () => ({ style: { paddingLeft: 12, paddingRight: 24 } }),
            render: (_, r) => {
                const transactionDate = getLedgerTransactionDate(r);
                if (!transactionDate) return <span style={{ color: '#bbb' }}>—</span>;
                return <span style={{ whiteSpace: 'nowrap' }}>{dayjs(transactionDate).format('YYYY-MM-DD HH:mm')}</span>;
            }
        },
        {
            title: 'Encoded',
            key: 'encoded_at',
            width: 150,
            render: (_, r) => {
                const encoded = getLedgerEncodedAt(r);
                if (!encoded) return <span style={{ color: '#bbb' }}>—</span>;
                return (
                    <Tooltip title={`Encoded by ${r.transaction_type === 'SALES' ? (r.sales_order?.creator?.name || 'Unknown') : (r.palay_purchase?.creator?.name || 'Unknown')}`}>
                        <span style={{ whiteSpace: 'nowrap' }}>{dayjs(encoded).format('YYYY-MM-DD HH:mm')}</span>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Confirmed',
            dataIndex: 'created_at',
            key: 'date',
            width: 150,
            render: (date, r) => {
                // Sales use their confirmation timestamp; purchases use the
                // journal posting timestamp. Standalone moves use the ledger.
                const confirmed = getLedgerConfirmedAt(r) || date;
                if (!confirmed) return <span style={{ color: '#bbb' }}>—</span>;
                const who = r.transaction_type === 'SALES'
                    ? (r.sales_order?.confirmer?.name || r.user?.name)
                    : r.user?.name;
                const stamp = <span style={{ whiteSpace: 'nowrap' }}>{dayjs(confirmed).format('YYYY-MM-DD HH:mm')}</span>;
                return who ? <Tooltip title={`Confirmed by ${who}`}>{stamp}</Tooltip> : stamp;
            }
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 90,
        },
        {
            title: 'Reference', key: 'ref', render: (_, r) => {
                if (r.batch_no) return r.batch_no;
                if (r.remarks) {
                    // remarks format: "Sales Order - SO-001" or "Palay Purchase - TKT-001"
                    const match = r.remarks.match(/[-–]\s*(.+)$/);
                    if (match) return match[1].trim();
                }
                return '—';
            }
        },
        {
            title: 'Customer / Supplier',
            key: 'customer',
            render: (_, r) => getLedgerCustomerName(r),
        },
        {
            title: 'Item',
            dataIndex: ['item', 'name'],
            key: 'item',
            width: 140,
            onHeaderCell: () => ({ style: { paddingLeft: 12, paddingRight: 24 } }),
            onCell: () => ({ style: { paddingLeft: 12, paddingRight: 24 } }),
        },
        {
            title: 'Type', dataIndex: 'transaction_type', key: 'type', render: (type) => (
                <Tag color={type === 'INITIAL_STOCK' ? 'green' : 'blue'}>{type === 'IN' ? 'PURCHASE_IN' : type}</Tag>
            )
        },
        { title: 'Qty', dataIndex: 'quantity', key: 'qty', render: (val) => Number(val).toLocaleString() },
        { title: 'Unit', key: 'unit', render: (_, record) => getLedgerUnitLabel(record) },
        { title: 'Location', dataIndex: ['location', 'name'], key: 'loc' },
    ];

    const StatCard = ({ title, value, prefix, suffix, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {prefix}{Number(value).toLocaleString()}{suffix}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Inventory & Stock Cards</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Track every palay lot and movement across branches from one place.</Typography.Text>
                    <div style={{ marginTop: 6 }}>
                        <Typography.Text type="secondary">{scopeLabel}</Typography.Text>
                    </div>
                </div>
                {!isStaff && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={() => { fetchPalayLots(); setIsModalOpen(true); }}
                        style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px', width: isMobile ? '100%' : 'auto' }}
                    >
                        Add Stock Adjustment
                    </Button>
                )}
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Tracked Items"
                        value={filteredLevels.length}
                        icon={<i className="fa-solid fa-boxes-stacked"></i>}
                        color="#3b82f6"
                        subText={`Across ${locationCoverage || '0'} locations`}
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Total Stock"
                        value={totalStock}
                        suffix=" kg"
                        icon={<i className="fa-solid fa-warehouse"></i>}
                        color="#10b981"
                        subText="Consolidated weight"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Low Stock Alerts"
                        value={lowStockCount}
                        icon={<i className="fa-solid fa-triangle-exclamation"></i>}
                        color={lowStockCount ? "#ef4444" : "#10b981"}
                        subText={lowStockCount ? "Attention needed" : "All stocks healthy"}
                    />
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                <Col xs={24}>
                    <Card
                        className="premium-card"
                        bodyStyle={{ padding: 0 }}
                        title={<span style={{ fontWeight: 700 }}>Current Stock Levels</span>}
                        extra={
                            <Space direction={isMobile ? 'vertical' : 'horizontal'} size={8} style={isMobile ? { width: '100%' } : undefined}>
                                <Select
                                    value={scopeBranchId}
                                    onChange={(value) => { setScopeBranchId(value); setScopeLocationId('all'); }}
                                    style={{ width: isMobile ? '100%' : 200 }}
                                    options={[
                                        { label: 'All branches', value: 'all' },
                                        ...branches.map((b) => ({ label: b.name, value: b.id })),
                                    ]}
                                />
                                <Select
                                    value={scopeLocationId}
                                    onChange={(value) => setScopeLocationId(value)}
                                    style={{ width: isMobile ? '100%' : 200 }}
                                    options={[
                                        { label: 'All locations', value: 'all' },
                                        ...locations
                                            .filter((loc) => scopeBranchId === 'all' || Number(loc.branch_id) === Number(scopeBranchId))
                                            .map((loc) => ({ label: loc.name, value: loc.id })),
                                    ]}
                                />
                                <Select
                                    value={stockCategoryFilter}
                                    onChange={(value) => setStockCategoryFilter(value)}
                                    style={{ width: isMobile ? '100%' : 190 }}
                                    options={[
                                        { label: 'All categories', value: 'all' },
                                        ...itemCategories.map((name) => ({ label: name, value: name })),
                                    ]}
                                />
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Search item, ID, reference, customer..."
                                    allowClear
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{ width: isMobile ? '100%' : 280, borderRadius: 8 }}
                                />
                                <RangePicker
                                    value={ledgerDateRange}
                                    onChange={(dates) => setLedgerDateRange(dates || [null, null])}
                                    allowClear
                                    suffixIcon={<CalendarOutlined />}
                                    placeholder={['Order from', 'Order to']}
                                    style={{ borderRadius: 8, width: isMobile ? '100%' : 260 }}
                                    presets={[
                                        { label: 'This Week', value: [dayjs().startOf('week'), dayjs().endOf('week')] },
                                        { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                                        { label: 'Last Month', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                                        { label: 'Last 3 Months', value: [dayjs().subtract(3, 'month').startOf('day'), dayjs()] },
                                    ]}
                                />
                                <ExportButton
                                    filename={`movements-${ledgerDateRange[0] ? ledgerDateRange[0].format('YYYY-MM-DD') : 'all'}-to-${ledgerDateRange[1] ? ledgerDateRange[1].format('YYYY-MM-DD') : 'all'}`}
                                    sheetName="Stock Movements"
                                    title="Inventory Stock Movements"
                                    subtitle={[
                                        scopeLabel,
                                        ledgerDateRange[0] && ledgerDateRange[1]
                                            ? `${ledgerDateRange[0].format('MMM D, YYYY')} – ${ledgerDateRange[1].format('MMM D, YYYY')}`
                                            : 'All dates',
                                    ].join(' | ')}
                                    headers={['Transaction Date', 'Encoded', 'Confirmed', 'ID', 'Reference', 'Customer / Supplier', 'Item', 'Type', 'Qty', 'Unit', 'Location']}
                                    getData={getLedgerExportRows}
                                    style={{ width: isMobile ? '100%' : 'auto', borderRadius: 8 }}
                                />
                            </Space>
                        }
                    >
                        <Table
                            columns={stockColumns}
                            dataSource={filteredLevels}
                            rowKey="id"
                            pagination={{ defaultPageSize: 8, showSizeChanger: true, pageSizeOptions: ['8', '10', '20', '50', '100'] }}
                            loading={loading}
                            size={isMobile ? 'small' : 'middle'}
                            scroll={isMobile ? { x: 800 } : undefined}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} style={{ marginTop: 8 }}>
                <Col xs={24}>
                    <Card
                        className="premium-card"
                        bodyStyle={{ padding: 0 }}
                        title={<span style={{ fontWeight: 700 }}>{selectedItem ? `Movement History: ${selectedItem.name}` : 'Recent Movements'}</span>}
                    >
                        <Table
                            columns={ledgerColumns}
                            dataSource={filteredLedger}
                            rowKey="id"
                            pagination={{ defaultPageSize: 8, showSizeChanger: true, pageSizeOptions: ['8', '10', '20', '50', '100'] }}
                            size={isMobile ? 'small' : 'middle'}
                            scroll={{ x: 1200 }}
                        />
                    </Card>
                </Col>
            </Row>

            <Modal
                title="Stock Adjustment"
                open={isModalOpen}
                onCancel={() => { setIsModalOpen(false); form.resetFields(); setAdjustmentCategoryFilter(null); }}
                onOk={() => form.submit()}
                confirmLoading={submitLoading}
                destroyOnClose
                okText="Submit Adjustment"
                width={560}
                okButtonProps={{ style: { height: 40, borderRadius: 8, fontWeight: 600 } }}
                cancelButtonProps={{ style: { height: 40, borderRadius: 8 } }}
            >
                <Form form={form} layout="vertical" onFinish={handleAddStock} initialValues={{ adjustment_type: 'STOCK_IN' }}>
                    <Form.Item name="adjustment_type" label="Adjustment Type" rules={[{ required: true }]}>
                        <Select style={{ height: 40 }}>
                            <Select.Option value="STOCK_IN">
                                <Space><ArrowUpOutlined style={{ color: '#10b981' }} /><span>Stock In — Add inventory</span></Space>
                            </Select.Option>
                            <Select.Option value="STOCK_OUT">
                                <Space><ArrowDownOutlined style={{ color: '#ef4444' }} /><span>Stock Out — Remove inventory</span></Space>
                            </Select.Option>
                            <Select.Option value="CORRECTION">
                                <Space><EditOutlined style={{ color: '#f59e0b' }} /><span>Correction — Fix quantity error</span></Space>
                            </Select.Option>
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="branch_id" label="Branch" rules={[{ required: true }]}>
                                <Select
                                    placeholder="Select branch"
                                    options={branches.map((b) => ({ label: b.name, value: b.id }))}
                                    onChange={() => form.setFieldsValue({ location_id: undefined })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="location_id" label="Location" rules={[{ required: true }]}>
                                <Select placeholder="Select location" options={filteredLocations.map((loc) => ({ label: loc.name, value: loc.id }))} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Category (filter)">
                                <Select
                                    allowClear
                                    placeholder="Filter items by category"
                                    options={itemCategories.map((name) => ({ label: name, value: name }))}
                                    value={adjustmentCategoryFilter}
                                    onChange={(value) => { setAdjustmentCategoryFilter(value || null); form.setFieldsValue({ item_id: undefined }); }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="item_id" label="Item" rules={[{ required: true }]}>
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Select item"
                                    options={filteredItems.map((item) => ({
                                        label: `${item.name} (${item.code})`,
                                        value: item.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={12}>
                            <Form.Item name="quantity" label="Quantity" rules={[{ required: true, message: 'Enter quantity' }]}>
                                <InputNumber style={{ width: '100%', height: 40 }} min={0.01} precision={2} placeholder="0.00" />
                            </Form.Item>
                        </Col>
                        <Col xs={12}>
                            <Form.Item name="cost" label="Unit Cost (optional)">
                                <InputNumber style={{ width: '100%', height: 40 }} min={0} precision={2} placeholder="0.00" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="remarks" label="Remarks">
                        <Input.TextArea rows={2} placeholder="Reason for adjustment (e.g., physical count, damaged goods, opening balance)" style={{ borderRadius: 8 }} />
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) =>
                            prev.item_id !== cur.item_id ||
                            prev.adjustment_type !== cur.adjustment_type ||
                            prev.branch_id !== cur.branch_id ||
                            prev.location_id !== cur.location_id
                        }
                    >
                        {({ getFieldValue }) => {
                            const itemId = getFieldValue('item_id');
                            const adjType = getFieldValue('adjustment_type');
                            const branchId = getFieldValue('branch_id');
                            const locationId = getFieldValue('location_id');
                            if (!itemId || !isPalayItem(itemId)) return null;

                            if (adjType === 'STOCK_IN') {
                                return (
                                    <Form.Item
                                        name="lot_number"
                                        label="Palay Lot Number"
                                        extra="Required for Palay stock-in so this lot appears in Sales Order lot selection."
                                        rules={[{ required: true, message: 'Enter a lot number for this palay stock-in' }]}
                                    >
                                        <Input placeholder="e.g., LOT-2026-001" style={{ borderRadius: 8 }} />
                                    </Form.Item>
                                );
                            }

                            if (adjType === 'STOCK_OUT') {
                                const unitLabel = getItemUnitLabel(itemId);
                                const availableLots = palayLots.filter((lot) =>
                                    lot.item_id === itemId &&
                                    (!branchId || lot.branch_id === branchId) &&
                                    (!locationId || lot.location_id === locationId)
                                );
                                return (
                                    <Form.Item
                                        name="lot_number"
                                        label="Palay Lot"
                                        extra="Select the lot to deduct stock from."
                                        rules={[{ required: true, message: 'Select a palay lot for stock-out' }]}
                                    >
                                        <Select
                                            placeholder="Select lot..."
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                            style={{ borderRadius: 8 }}
                                            options={availableLots.map((lot) => ({
                                                label: `${lot.lot_number}${lot.storage_code ? ` (${lot.storage_code})` : ''} - ${lot.purchase?.partner?.name || 'No supplier'} - ${Number(lot.current_quantity).toLocaleString()} ${unitLabel} remaining`,
                                                value: lot.lot_number,
                                            }))}
                                            notFoundContent={
                                                <span style={{ color: '#999', fontSize: 13 }}>
                                                    No lots found for this item/branch/location
                                                </span>
                                            }
                                        />
                                    </Form.Item>
                                );
                            }

                            if (adjType === 'CORRECTION') {
                                const unitLabel = getItemUnitLabel(itemId);
                                const correctionLots = allPalayLots.filter((lot) =>
                                    lot.item_id === itemId &&
                                    (!branchId || lot.branch_id === branchId) &&
                                    (!locationId || lot.location_id === locationId)
                                );
                                return (
                                    <Form.Item
                                        name="lot_number"
                                        label="Palay Lot to Correct"
                                        extra="Select the lot whose quantity you want to correct. The quantity entered will replace the current lot quantity."
                                        rules={[{ required: true, message: 'Select a palay lot to correct' }]}
                                    >
                                        <Select
                                            placeholder="Select lot..."
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                            style={{ borderRadius: 8 }}
                                            options={correctionLots.map((lot) => ({
                                                label: `${lot.lot_number}${lot.storage_code ? ` (${lot.storage_code})` : ''} - ${lot.purchase?.partner?.name || 'No supplier'} - current: ${Number(lot.current_quantity).toLocaleString()} ${unitLabel}${lot.status === 'CLOSED' ? ' [CLOSED]' : ''}`,
                                                value: lot.lot_number,
                                            }))}
                                            notFoundContent={
                                                <span style={{ color: '#999', fontSize: 13 }}>
                                                    No lots found for this item/branch/location
                                                </span>
                                            }
                                        />
                                    </Form.Item>
                                );
                            }

                            return null;
                        }}
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default StockCardPage;
